import {
  Channels,
  Channel,
  SortOption,
  Choice,
  KeyValuePair,
  PACKAGE_INDICATORS
} from "@directv-digital/dds.services.channel-lineup-provider";
import { mockLocalData, mockRsnData } from "../utils/fallbackData";
import { deepClone } from "../utils/utilityFunctions";
import { filterCategoricalChannels, orderCategoriesExact, queryChannels } from "./filterMethods";
import { applyEntertainmentCategoryFallback, bucketChannelsByCategory, isChannelInAnyCategory } from "lib/helper/channels-service-helpers";
import { IPackage } from "../chnlListingPage.props";
import { handleNationalLocalDmaFilterChlup } from "lib/service/channels/get-local-national-dma-eligible-channels";

export interface IChannelsUI {
  bannerChannels: {
    [key: string]: Channel[] | any;
  };
  cmprGridChannelsAZ?: {
    ["INCLUDED_CHANNELS"]?: any[]; // Channel[] | any[];
    ["ADD_ON_CHANNELS"]?: any[] // Channel[] | any[];
    ["MYFREE_DTV"]?: any[];
  };
  cmprGridChannelsNum?: {
    ["INCLUDED_CHANNELS"]?: any[]; // Channel[] | any[];
    ["ADD_ON_CHANNELS"]?: any[] // Channel[] | any[];
    ["MYFREE_DTV"]?: any[];
  };
  cmprGridChannelsCategorical?: {
    [key: string]: Channel[] | any;
  }
}

export interface ChlupState {
  activeView: 'categories' | 'packages';
  cmprGridViewMode: SortOption;
  channels: any[];
  embeddedSvods: any[];
  searchChoicesSvods: Choice[];
  bannerChannels?: IChannelsUI["bannerChannels"];
  cmprGridChannelsAZ?: IChannelsUI["cmprGridChannelsAZ"];
  initialCmprGridChannelsAZ?: IChannelsUI["cmprGridChannelsAZ"]; // used to keep original data for filtering when in packages view
  cmprGridChannelsNum?: IChannelsUI["cmprGridChannelsNum"];
  initialCmprGridChannelsNum?: IChannelsUI["cmprGridChannelsNum"]; // used to keep original numeric data for filtering when in packages view
  cmprGridChannelsCategorical?: IChannelsUI["cmprGridChannelsCategorical"];
  initialCmprGridChannelsCategorical?: IChannelsUI["cmprGridChannelsCategorical"]; // used to keep original categorical data for filtering when in packages view
  searchChoices?: Choice[];
  needsUpdate: {
    banner: boolean;
    grid: boolean;
  },
  myFreeKey?: string;
  cache: {
    localChnls: any[];
    rsnChnls: any[];
    bannerCategories: KeyValuePair[];
    bannerPackages: IPackage[];
    gridCategoriesAZ: KeyValuePair[];
    gridCategories: KeyValuePair[];
    gridPackages: IPackage[];
    myFreeKey?: string;
  }
  currentDma: string;
  genreNationalMapping?: any[];
  genreMapping?: Record<string, string>;
}

// type ChannelTree = {
//   [category: string]: any[] | ChannelTree;
// }
// export interface IChannelsUI {
//   bannerChannels: ChannelTree;
//   cmprGridChannelsAZ?: ChannelTree;
//   cmprGridChannelsCategorical?: ChannelTree;
// }

export type ChlupStateActions =
  | { type: 'SET_LOADING_LOCAL_RSN_CHANNELS' }
  | { type: 'ADD_LOCAL_RSN_CHANNELS', data: { local: any[], rsn: any[], currentDma: string, genreNationalMapping?: any[] } }
  | { type: 'UPDATE_COMPONENT_VIEW',
      data: {
        selectedToggle?: string,
        viewMode?: SortOption
      }
    }
  | { type: 'REFRESH_BANNER' }
  | { type: 'REFRESH_PKGS_GRID' }

export interface ChannelFilter {
  product?: 'stream' | 'satellite';
  searchList?: Choice[];
  categories?: string[];
  chnlPackages?: string[];
  addOnPromoAny?: boolean;
  includedAny?: boolean;
  local?: boolean;
  rsn?: boolean;
  myFreeDtv?: boolean;
  genrePacks?: string[] | 'all';
};

/** ----------------------------------------------------------------------------
 * Helper Functions
 */

function getComponentView(selectedToggle?: string) {
  if (selectedToggle === 'SIGNATURE PACKAGES' || selectedToggle === 'PACKAGES') {
    return 'packages';
  } else if (selectedToggle === "CATEGORY" || selectedToggle === 'GENRE PACKS' || selectedToggle === 'ALL CHANNELS') {
    return 'categories';
  }
};

/** ***** Banner --------------------------------------------------------------- */
/**
 * Create display-ready map of channels for the banner view
 * - See IChannelsUI["bannerChannels"] for format
 * - Keep as optimized as possible
 */
function getBannerChannels(
  channels: any[],
  categories: KeyValuePair[], // either genreAndMiniCategories or sigBannerCategories
  // packages: IPackage[], // either genrePackages or signaturePackages
  myFreeKey: string,
  currentDma: string,
  genreNationalMapping?: any[],
  genreMapping?: Record<string, string>
): IChannelsUI["bannerChannels"] {
  try {
    const chnls: IChannelsUI["bannerChannels"] = {};
    if(myFreeKey) {
      chnls[myFreeKey] = [];
    }
    for (const channel of channels) {
      for (const pkg of channel?.chnlPackages ?? []) {
        const keyMatch = (pkg?.match(/ATT_TV_(.*)/)?.[1] || "").toUpperCase();

        if (categories?.some(ctg => ctg?.key === keyMatch)) {
          const ctgKey = categories?.find(ctg => ctg?.key === pkg?.match(/ATT_TV_(.*)/)?.[1])?.key;

          // console.debug("[ChlupStore] adding", channel?.name, " to ", ctgKey);
          if (!chnls[ctgKey]) {
            chnls[ctgKey] = [channel] as any[];
          } else {
            chnls[ctgKey].push(channel);
          }
        }
      }
      // add MyFree Channels into MyFree section
      const isMyFreeDTV = channel?.isMyFreeDTV;
      if (isMyFreeDTV && myFreeKey) {
        chnls[myFreeKey].push(channel);
      }
    }
    // Genre Local/National DMA Eligibility check - start
    const shortNameToQueryParam: Record<string, string> = {};
    if (genreMapping) {
      for (const [qp, shortName] of Object.entries(genreMapping)) {
        shortNameToQueryParam[shortName?.toUpperCase()] = qp;
        const withoutPrefix = shortName?.toUpperCase()?.replace(/^ATT_TV_/, '');
        if (withoutPrefix) {
          if (process.env.NODE_ENV !== 'production' && shortNameToQueryParam[withoutPrefix] && shortNameToQueryParam[withoutPrefix] !== qp) {
            console.warn(`[ChlupStore] genreMapping key collision on "${withoutPrefix}": overwriting "${shortNameToQueryParam[withoutPrefix]}" with "${qp}"`);
          }
          shortNameToQueryParam[withoutPrefix] = qp;
        }
      }
    }
    // eslint-disable-next-line guard-for-in
    for (const ctg in chnls) {
      try {
        const genrePackChannels = [...(chnls[ctg] || [])];
        const packageKey = shortNameToQueryParam[ctg?.toUpperCase()] || ctg;
        const filteredChannels = handleNationalLocalDmaFilterChlup({
          nationalChannels: genrePackChannels,
          currentDma,
          nationalMapping: genreNationalMapping,
          packageKey,
          categoryKey: ctg
        });
        chnls[ctg] = Array.isArray(filteredChannels) ? filteredChannels : genrePackChannels;
      } catch (err) {
        console.error(`[ChlupStore] Error while handleNationalLocalDmaFilterChlup for category ${ctg}:`, err);
        chnls[ctg] = chnls[ctg] || [];
      }
    }
    // Genre Local/National DMA Eligibility check - end
    console.debug(`[ChlupStore] getBannerChannels finished`, chnls)
    return chnls;
  } catch (e) {
    console.error("[ChlupStore] Error in getBannerChannels", e);
    return {};
  }
};

const edspSortKeyArray = [
  "SPORTS", "RSN", "LOCAL",
  "ENTERTAINMENT", "FAMILY_AND_KIDS", "MOVIES_AND_EVENT", "NEWS_AND_INFORMATION", "PREMIUM_TV",
  "ADD_ON_CHANNELS"
];

export function getDisplayedEdspBannerCategories(
  selectedCategories: KeyValuePair[],
  allCategoriesKV: KeyValuePair[],
  isLoadingLocalRsn: boolean
) {
  const displayCategories: KeyValuePair[] = [];

  if (selectedCategories?.length === 0) {
    return allCategoriesKV;
  }
  const selectedCategoryKeys = selectedCategories?.map(ctg => ctg?.key);

  for (const ctg of allCategoriesKV) {
    if (ctg?.key === "ADD_ON_CHANNELS") {
      displayCategories.push(ctg);
    }
    if (selectedCategoryKeys?.includes(ctg.key)) {
      displayCategories.push(ctg);
    }
  }

  if (isLoadingLocalRsn) {
    displayCategories.push({
      key: "LOCAL",
      value: "Local Channels"
    });
    displayCategories.push({
      key: "RSN",
      value: "Regional Sports"
    });
  }

  return orderCategoriesExact(displayCategories, edspSortKeyArray);
};

/** ***** Package Comparison Grid----------------------------------------------- */
/**
 * Create display-ready map of channels for the Packages tab ALPHABETICAL View
 * - See IChannelsUI["bannerChannels"] for format
 */
function getCmprGridChannelsAZ(
  channels: any[],
  categories: KeyValuePair[],
  packages: IPackage[],
  myFreeKey: string
) {
  try {
    const chnls: IChannelsUI["cmprGridChannelsAZ"] = {};
    const indicatorIxs = packages.reduce((pkgs, pkg) => {
      pkgs.push(pkg?.packageIndicatorIndex);
      return pkgs;
    }, []);

    for (const channel of channels) {
      if (channel.category === undefined || channel.category === "") {
        console.warn("[ChlupStore] getCmprGridChannelsAZ: channel not added, category is undefined or empty", channel?.name);
        continue;
      }

    const chnlCategory = channel.category?.toUpperCase();
    // Structural LOCAL/RSN membership — detect via the flags set on the built
    // lineup channel (buildLocalRsnChannel); the case-normalized `category` scalar
    // is kept as a Stage-1 safety net (every built LOCAL/RSN channel carries both).
    const isLocalRsn = Boolean(channel?.isLocal || channel?.isRsn) || chnlCategory === "LOCAL" || chnlCategory === "RSN";
    const isInCategory = isChannelInAnyCategory(channel, categories as any);
    const isIncAny = // isIncAny === ENGLISH
      isLocalRsn || channel?.isInclAny
    const isAddOnPromoAny =
      !isLocalRsn && channel?.isAddOnPromoAny;
    const isMyFreeDTV = channel?.isMyFreeDTV;
    // const c = new Channel(channel);

      if (isIncAny && isInCategory && !isMyFreeDTV && !channel?.signatureExcluded) {
        const c = {
          ...channel,
          type: "Included",
          uuid: `Included-${channel?.uuid}`,
          indicators: channel?.indicators
            ?.filter((_, ix) => indicatorIxs?.includes(ix))
            ?.map(ind => (ind === PACKAGE_INDICATORS.IN ? PACKAGE_INDICATORS.IN : null))
        }
        if (!chnls?.INCLUDED_CHANNELS) {
          chnls.INCLUDED_CHANNELS = [c] as any[];
        } else {
          chnls.INCLUDED_CHANNELS.push(c);
        }
      }
      if (isAddOnPromoAny && isInCategory && !isMyFreeDTV) {
        const c = {
          ...channel,
          uuid: `AddOn-${channel?.name}-${channel?.chnlNum}`,
          type: "AddOn",
          indicators: channel?.indicators
            ?.filter((_, ix) => indicatorIxs?.includes(ix))
        }
        if (!chnls?.ADD_ON_CHANNELS) {
          chnls.ADD_ON_CHANNELS = [c];
        } else {
          chnls.ADD_ON_CHANNELS.push(c);
        }
      }
      if (isMyFreeDTV && myFreeKey) {
        const c = {
          ...channel,
          uuid: `MyFreeDTV-${channel?.name}-${channel?.chnlNum}`,
          type: "MyFreeDTV",
          indicators: indicatorIxs?.map(_ix => 1)
        };
        if (!chnls?.[myFreeKey]) {
          chnls[myFreeKey] = [c];
        } else {
          chnls[myFreeKey].push(c);
        }
      }
    }
    console.debug(`[ChlupStore] getCmprGridChannelsAZ finished`, chnls)
    return chnls;
  } catch (e) {
    console.error("[ChlupStore] Error: in getCmprGridChannelsAZ", e);
    return {};
  }
}
/**
 *
 * @param channels
 * @param categories
 * @param packages
 * @returns channels in a format ready for the Packages tab NUMERIC View
 * Create display-ready map of channels for the Packages tab NUMERIC View
 * - See IChannelsUI["cmprGridChannelsNum"] for format
 * - Collects included and add-on channels separately
 * - Sorts both arrays by channel number
 * - Uses packageIndicatorIndex to filter indicators
 * - Returns an object with two arrays: INCLUDED_CHANNELS and ADD_ON_CHANNELS
 */
function getCmprGridChannelsNum(
  channels: any[],
  categories: KeyValuePair[],
  packages: IPackage[],
  myFreeKey: string
) {
  try {
    const chnls: IChannelsUI["cmprGridChannelsNum"] = {};
    const indicatorIxs = packages.reduce((pkgs, pkg) => {
      pkgs.push(pkg?.packageIndicatorIndex);
      return pkgs;
    }, []);

    // Collect included and add-on channels
    const includedChannels: any[] = [];
    const addOnChannels: any[] = [];
    const myFreeDtvChannels: any[] = [];

    for (const channel of channels) {
      if (channel.category === undefined || channel.category === "") {
        console.warn("[ChlupStore] getCmprGridChannelsNum: channel not added, category is undefined or empty", channel?.name);
        continue;
      }

      const chnlCategory = channel.category?.toUpperCase();
      // Structural LOCAL/RSN membership — flags first, scalar `category` as Stage-1 fallback.
      const isLocalRsn = Boolean(channel?.isLocal || channel?.isRsn) || chnlCategory === "LOCAL" || chnlCategory === "RSN";
      const isInCategory = isChannelInAnyCategory(channel, categories as any);
      const isIncAny =
        isLocalRsn || channel?.isInclAny;
      const isAddOnPromoAny =
        !isLocalRsn && channel?.isAddOnPromoAny;
      const isMyFreeDTV = channel?.isMyFreeDTV;

      if (isIncAny && isInCategory && !isMyFreeDTV && !channel?.signatureExcluded) {
        const c = {
          ...channel,
          type: "Included",
          uuid: `Included-${channel?.uuid}`,
          indicators: channel?.indicators
            ?.filter((_, ix) => indicatorIxs?.includes(ix))
            ?.map(ind => (ind === PACKAGE_INDICATORS.IN ? PACKAGE_INDICATORS.IN : null))
        };
        includedChannels.push(c);
      }
      if (isAddOnPromoAny && isInCategory && !isMyFreeDTV) {
        const c = {
          ...channel,
          uuid: `AddOn-${channel?.name}-${channel?.chnlNum}`,
          type: "AddOn",
          indicators: channel?.indicators
            ?.filter((_, ix) => indicatorIxs?.includes(ix))
        };
        addOnChannels.push(c);
      }
      if (isMyFreeDTV) {
        const c = {
          ...channel,
          uuid: `MyFreeDTV-${channel?.name}-${channel?.chnlNum}`,
          type: "MyFreeDTV",
          indicators: indicatorIxs?.map(_ix => 1)
        };
        myFreeDtvChannels.push(c);
      }
    }
    // Sort both arrays by channel number
    includedChannels.sort((a, b) => Number(a.chnlNum) - Number(b.chnlNum));
    addOnChannels.sort((a, b) => Number(a.chnlNum) - Number(b.chnlNum));
    myFreeDtvChannels.sort((a, b) => Number(a.chnlNum) - Number(b.chnlNum));
    if (includedChannels.length > 0) {
      chnls.INCLUDED_CHANNELS = includedChannels;
    }
    if (addOnChannels.length > 0) {
      chnls.ADD_ON_CHANNELS = addOnChannels;
    }
    if (myFreeDtvChannels.length > 0 && myFreeKey) {
      chnls[myFreeKey] = myFreeDtvChannels;
    }

    console.debug(`[ChlupStore] getCmprGridChannelsNum finished`, chnls);
    return chnls;
  } catch (e) {
    console.error("[ChlupStore] Error: in getCmprGridChannelsNum", e);
    return {};
  }
}

/**
 * Create display-ready map of channels for the Packages tab CATEGORICAL View
 * - See IChannelsUI["bannerChannels"] for format
 */
function getCmprGridChannelsCategorical(
  channels: any[],
  categories: KeyValuePair[],
  packages: IPackage[],
  myFreeKey: string
) {
  try {
    const chnls: IChannelsUI["cmprGridChannelsCategorical"] = {};
    const indicatorIxs = packages.reduce((pkgs, pkg) => {
      pkgs.push(pkg?.packageIndicatorIndex);
      return pkgs;
    }, []);

  // Genre accordions: one call, CMS-driven. Each channel appears under every
  // label whose engCategories it matches. No key translation, no hardcoding.
  const genreBuckets = bucketChannelsByCategory(channels, categories as any);
  for (const [label, labelChannels] of Object.entries(genreBuckets)) {
    for (const channel of labelChannels) {
      // Structural LOCAL/RSN membership — flags first, scalar `category` as Stage-1 fallback.
      const isLocalRsn = Boolean(channel?.isLocal || channel?.isRsn) || ["LOCAL","RSN"].includes((channel?.category||"").toUpperCase());
      const isIncAny = channel?.isInclAny || isLocalRsn;
      // AddOn gate matches the A-Z / Channel-# views (and pre-Phase-G prod): a
      // non-local channel that is both Included AND an add-on shows in both
      // sub-sections. Phase G's categorical rewrite incidentally flipped this to
      // !isIncAny; restored here for cross-view + prod parity.
      const isAddOnPromoAny = !isLocalRsn && channel?.isAddOnPromoAny;
      if (channel?.isMyFreeDTV) continue; // handled below
      if (isIncAny && !channel?.signatureExcluded) {
        const c = {
          ...channel,
          uuid: `Included-${channel?.name}-${channel?.chnlNum}-${label}`,
          type: "Included",
          indicators: channel?.indicators
            ?.filter((_: any, ix: number) => indicatorIxs?.includes(ix))
            ?.map((ind: any) => ind === PACKAGE_INDICATORS.IN ? PACKAGE_INDICATORS.IN : null)
        };
        if (!chnls[label]) chnls[label] = [] as any[];
        (chnls[label] as any[]).push(c);
      }
      if (isAddOnPromoAny) {
        const c = {
          ...channel,
          uuid: `AddOn-${channel?.name}-${channel?.chnlNum}-${label}`,
          type: "AddOn",
          indicators: channel?.indicators?.filter((_: any, ix: number) => indicatorIxs?.includes(ix))
        };
        if (!chnls["ADD_ON_CHANNELS"]) chnls["ADD_ON_CHANNELS"] = {};
        if (!(chnls["ADD_ON_CHANNELS"] as any)[label]) (chnls["ADD_ON_CHANNELS"] as any)[label] = [] as any[];
        (chnls["ADD_ON_CHANNELS"] as any)[label].push(c);
      }
    }
  }

  // Structural channels: RSN (Regional Sports) and Local — injected by the
  // zip/DMA API. Membership is detected via the structural flags (set on the built
  // channel); the scalar `category` is a Stage-1 fallback. The resolved key stays
  // "RSN"/"LOCAL" because that IS the canonical structural accordion section label.
  for (const channel of channels) {
    const structCat = channel?.isRsn
      ? "RSN"
      : channel?.isLocal
        ? "LOCAL"
        : (channel?.category || "").toUpperCase();
    if (structCat !== "RSN" && structCat !== "LOCAL") continue;
    if (channel?.isMyFreeDTV) continue;
    const c = {
      ...channel,
      uuid: `Structural-${channel?.name}-${channel?.chnlNum}-${structCat}`,
      type: "Included",
      indicators: channel?.indicators
        ?.filter((_: any, ix: number) => indicatorIxs?.includes(ix))
        ?.map((ind: any) => ind === PACKAGE_INDICATORS.IN ? PACKAGE_INDICATORS.IN : null)
    };
    if (!chnls[structCat]) chnls[structCat] = [] as any[];
    (chnls[structCat] as any[]).push(c);
  }

  // MyFree channels
  for (const channel of channels) {
    const isMyFreeDTV = channel?.isMyFreeDTV;
    if (isMyFreeDTV && myFreeKey) {
      const c = {
        ...channel,
        uuid: `MyFreeDTV-${channel?.name}-${channel?.chnlNum}`,
        type: "MyFreeDTV",
        indicators: indicatorIxs?.map((ix: any) => 1)
      };
      if (!chnls?.[myFreeKey])
        chnls[myFreeKey] = [c];
      else
        chnls[myFreeKey].push(c);
    }
  }

    return chnls;
  } catch (e) {
    console.error("[ChlupStore] Error: in getCmprGridChannelsCategorical", e);
    return {};
  }
}

/** ***** Search bar ---------------------------------------------------------- */
export function getSearchChoices(channels: any[], type?: string): Choice[] {
  const choices = [];
  for (const c of channels) {
    choices.push({
      displayText: c.name,
      category: c.category,
      chnlPackages: c.chnlPackages,
      value: c.name,
      type
    });
  }
  return choices;
}

/** ----------------------------------------------------------------------------
 * STATE INITIALIZATION
 * - Only data for the initial view is loaded immediately. Channel data for other
 *   pages is loaded idly thru the needsUpdate flag.
 */
export function initializeChlupStore(data): ChlupState {
  const initComponentView = getComponentView(data?.selectedToggle);
  // Category fallback (one pass, all three signature views): includable channels
  // whose categories[] match no configured content accordion (e.g. format-only
  // "DIRECTV 4K Live") are dropped by the AZ/Channel#/Categorical gates. Bucket
  // them under Entertainment here, on the shared channel list + the SAME
  // gridCategories the builders match against, so it also covers the reducer's
  // lazy rebuilds (which re-derive from state.channels + cache.gridCategories).
  // Clone-on-write: the helper returns a NEW array (WAT-11076 — channel ENTRIES may be frozen
  // cache objects, so it never mutates them in place). We assign that array onto `data.channels`
  // so the builders + the reducer's lazy rebuilds below read the fallback-applied channels.
  // INVARIANT (re: input mutation): `data` here is the client-side, JSON-parsed /api/channel-data
  // envelope — a fresh, mutable object — NOT the server's deep-frozen cache object (that exists
  // only server-side; the cache is never handed to this client store). So this property write is
  // safe, and the repo's `no-param-reassign` is configured `props:false`, which permits it. If a
  // future caller ever passes a frozen `data` envelope, switch to a local copy + thread it through.
  if (data?.channels) {
    data.channels = applyEntertainmentCategoryFallback(data.channels, data?.gridCategories);
  }
  // Generated by Copilot- Start - 2026-03-13 13:38:18
  const initialCmprGridChannelsAZ = initComponentView === 'packages' ? getCmprGridChannelsAZ(
    data?.channels,
    data?.gridCategories,
    data?.gridPackages,
    data?.myFreeKey
  ) : {};
  // Generated by Copilot- End - 2026-03-13 13:38:18
  // Generated by Copilot- Start - 2026-03-13 18:30:40
  const initialCmprGridChannelsNum = initComponentView === 'packages' ? getCmprGridChannelsNum(
    data?.channels,
    data?.gridCategories,
    data?.gridPackages,
    data?.myFreeKey
  ) : {};
  // Generated by Copilot- End - 2026-03-13 18:30:40
  // Generated by Copilot- Start - 2026-03-13 19:27:37
  const initialCmprGridChannelsCategorical = initComponentView === 'packages' ? getCmprGridChannelsCategorical(
    data?.channels,
    data?.gridCategories,
    data?.gridPackages,
    data?.myFreeKey
  ) : {};
  // Generated by Copilot- End - 2026-03-13 19:27:37

  const clupState: ChlupState = {
    channels: data?.channels,
    embeddedSvods: data?.embeddedSvods,
    searchChoicesSvods: getSearchChoices(data?.embeddedSvods, "svod"),
    bannerChannels: initComponentView === 'categories' ? getBannerChannels(
      data?.channels,
      data?.bannerCategories,
      // data?.bannerPackages,
      data?.myFreeKey,
      data?.currentDma,
      data?.genreNationalMapping,
      data?.genreMapping
    ) : {},
    cmprGridChannelsAZ: initialCmprGridChannelsAZ,
    initialCmprGridChannelsAZ: deepClone(initialCmprGridChannelsAZ), // used to keep original data for filtering when in packages view
    cmprGridChannelsNum: initialCmprGridChannelsNum,
    initialCmprGridChannelsNum: deepClone(initialCmprGridChannelsNum), // used to keep original numeric data for filtering when in packages view
    cmprGridChannelsCategorical: initialCmprGridChannelsCategorical,
    initialCmprGridChannelsCategorical: deepClone(initialCmprGridChannelsCategorical), // used to keep original categorical data for filtering when in packages view
    searchChoices: getSearchChoices(data?.channels),
    cmprGridViewMode: data?.cmprGridViewMode,
    activeView: initComponentView,
    needsUpdate: {
      banner: true,
      grid: true
    },
    myFreeKey: data?.myFreeKey,
    cache: {
      localChnls: [],
      rsnChnls: [],
      bannerCategories: data?.bannerCategories,
      bannerPackages: data?.bannerPackages,
      gridCategoriesAZ: data?.gridCategoriesAZ,
      gridCategories: data?.gridCategories,
      gridPackages: data?.gridPackages,
      myFreeKey: data?.myFreeKey
    },
    currentDma: data?.currentDma,
    genreNationalMapping: data?.genreNationalMapping,
    genreMapping: data?.genreMapping
  };

  return clupState;
}

// Generated by Copilot- Start
function removeChannelsByAliases(channelsData: Record<string, any>, channelAliases: string[]) {
  const channelsDataCopy = deepClone(channelsData);
  for (const category in channelsDataCopy) {
    if (Array.isArray(channelsDataCopy[category])) {
      channelsDataCopy[category] = channelsDataCopy[category].filter(channel => !channelAliases.includes(channel.name));
    } else if (typeof channelsDataCopy[category] === 'object' && channelsDataCopy[category] !== null) {
      for (const subCategory in channelsDataCopy[category]) {
        if (Array.isArray(channelsDataCopy[category][subCategory])) {
          channelsDataCopy[category][subCategory] = channelsDataCopy[category][subCategory].filter(channel => !channelAliases.includes(channel.name));
        }
      }
    }
  }

  return channelsDataCopy;
}

function getNationalAliasesForLocalChannels(localChannels, genreNationalMapping) {
  return genreNationalMapping
    .filter(genreNational => localChannels.some(localChannel => localChannel.name === genreNational.channel))
    .flatMap(genreNational => genreNational.channelAliases || []);
}
// Generated by Copilot- End

/** ----------------------------------------------------------------------------
 * STATE REDUCER
 * - Handles all state updates for channels in the component
 * - Called as "dispatch({ type: 'ACTION_TYPE', data: { ... } })"
 */
export function chlupStoreReducer(
  state: ChlupState,
  action: ChlupStateActions
): ChlupState {
  if (action.type === 'UPDATE_COMPONENT_VIEW') {
    const newView = getComponentView(action.data.selectedToggle);
    console.info("[ChlupStore] updating component view to ", newView);

    if (newView === state.activeView) {
      return state; // no-op
    } else if (newView === 'categories') {
      return {
        ...state,
        activeView: newView,
        needsUpdate: {
          ...state.needsUpdate,
          banner: Object.keys(state.bannerChannels)?.length === 0
        }
      }
    } else if (newView === 'packages') {
      return !state.cmprGridChannelsAZ?.INCLUDED_CHANNELS || !state.cmprGridChannelsNum?.INCLUDED_CHANNELS || !state.cmprGridChannelsCategorical?.INCLUDED_CHANNELS ?
        {
          ...state,
          activeView: newView,
          cmprGridChannelsAZ: getCmprGridChannelsAZ(
            state?.channels,
            state?.cache?.gridCategories,
            state?.cache?.gridPackages,
            state?.myFreeKey
          ),
          cmprGridChannelsNum: getCmprGridChannelsNum(
            state?.channels,
            state?.cache?.gridCategories,
            state?.cache?.gridPackages,
            state?.myFreeKey
          ),
          cmprGridChannelsCategorical: getCmprGridChannelsCategorical(
            state?.channels,
            state?.cache?.gridCategories,
            state?.cache?.gridPackages,
            state?.myFreeKey
          ),
          needsUpdate: {
            ...state.needsUpdate,
            grid: (Object.keys(state?.cmprGridChannelsAZ)?.length === 0 || Object.keys(state?.cmprGridChannelsNum)?.length === 0 || Object.keys(state?.cmprGridChannelsCategorical)?.length === 0)
          }
        } :
        {
          ...state,
          activeView: newView,
          needsUpdate: {
            ...state.needsUpdate,
            grid: (Object.keys(state?.cmprGridChannelsAZ)?.length === 0 || Object.keys(state?.cmprGridChannelsNum)?.length === 0 || Object.keys(state?.cmprGridChannelsCategorical)?.length === 0)
          }
        }
    }

    return state;
  } else if (action.type === 'SET_LOADING_LOCAL_RSN_CHANNELS') {
    if (state.activeView === 'categories') {
      return {
        ...state,
        bannerChannels: filterCategoricalChannels(
          {
            local: false,
            rsn: false
          },
          state.bannerChannels
        )
      }
    }
    return {
      ...state,
      cmprGridChannelsAZ: {
        ...state.cmprGridChannelsAZ,
        ["INCLUDED_CHANNELS"]: queryChannels(
          {
            local: false,
            rsn: false
          },
          state.cmprGridChannelsAZ?.INCLUDED_CHANNELS
        )
      },
      cmprGridChannelsNum: {
        ...state.cmprGridChannelsNum,
        ["INCLUDED_CHANNELS"]: queryChannels(
          {
            local: false,
            rsn: false
          },
          state.cmprGridChannelsNum?.INCLUDED_CHANNELS
        )
      },
      cmprGridChannelsCategorical: {
        ...state.cmprGridChannelsCategorical,
        ["LOCAL"]: mockLocalData,
        ["RSN"]: mockRsnData
      }
    }
  } else if (action.type === 'ADD_LOCAL_RSN_CHANNELS') {
    if (action?.data?.local?.length === 0 && action?.data?.rsn?.length === 0) {
      console.warn("[ChlupStore] Adding Local RSN channels :: local and rsn channels have array length 0");
    }
    console.debug("[ChlupStore] adding local/rsn channels...", action?.data?.local, action?.data?.rsn);

    const newChannels = state.channels
      // filter out old LOCAL and RSN channels, filter in new ones
      ?.filter(c => c?.category !== "LOCAL" && c?.category !== "RSN")
      ?.concat(action.data.local, action.data.rsn)
      ?.sort(Channels.sortAlphabetically);

    const newChannelsRemoveMyHomeTeamDuplicates =
      newChannels?.filter(c => {
        if (c?.chnlPackages?.includes("ATT_TV_MYHOMETEAM")) {
          // if there's a matching rsn for the channel, don't add
          if (c?.category !== "RSN" && action?.data?.rsn?.some(rsn => rsn?.name?.toLowerCase() === c?.name?.toLowerCase())) {
            return false;
          }
        }
        return true;
      });
    // Generated by Copilot- Start
    const channelAliases = getNationalAliasesForLocalChannels(action.data.local, action?.data?.genreNationalMapping || []);

    return {
      ...state,
      channels: newChannelsRemoveMyHomeTeamDuplicates,
      cache: {
        ...state.cache,
        localChnls: action.data.local,
        rsnChnls: action.data.rsn
      },
      bannerChannels: getBannerChannels(
        newChannelsRemoveMyHomeTeamDuplicates,
        state.cache?.bannerCategories,
        state.cache?.myFreeKey,
        action?.data?.currentDma,
        action?.data?.genreNationalMapping,
        state.genreMapping
      ),
      cmprGridChannelsAZ: {
        ...state.cmprGridChannelsAZ,
        ["INCLUDED_CHANNELS"]: [...(state.initialCmprGridChannelsAZ?.INCLUDED_CHANNELS || [])]
          ?.filter(c => c?.category !== "LOCAL" && c?.category !== "RSN" && !channelAliases.includes(c?.name))
          ?.concat(action.data.local?.filter(c => !c?.signatureExcluded) || [], action.data.rsn)
          ?.sort(Channels.sortAlphabetically)
      },
      cmprGridChannelsNum: {
        ...state.cmprGridChannelsNum,
        ["INCLUDED_CHANNELS"]: [...(state.initialCmprGridChannelsNum?.INCLUDED_CHANNELS || [])]
          ?.filter(c => c?.category !== "LOCAL" && c?.category !== "RSN" && !channelAliases.includes(c?.name))
          ?.concat(action.data.local?.filter(c => !c?.signatureExcluded) || [], action.data.rsn)
          ?.sort(Channels.sortByChannelNumber)
      },
      cmprGridChannelsCategorical: {
        ...removeChannelsByAliases(state.initialCmprGridChannelsCategorical, channelAliases),
        ["LOCAL"]: action.data?.local?.filter(c => !c?.signatureExcluded)?.sort(Channels.sortAlphabetically),
        ["RSN"]: action.data?.rsn?.sort(Channels.sortAlphabetically)
      },
      searchChoices: getSearchChoices(newChannelsRemoveMyHomeTeamDuplicates),
      needsUpdate: {
        banner: false,
        grid: false
      }
    }
    // Generated by Copilot- End
  } else if (action.type === "REFRESH_BANNER") {
    if (!state.needsUpdate.banner) {
      return state;
    } // no-op
    console.info("[ChlupStore] refreshing banner..")
    return {
      ...state,
      bannerChannels: getBannerChannels(
        state?.channels,
        state.cache?.bannerCategories,
        state.cache?.myFreeKey,
        state.currentDma,
        state.genreNationalMapping,
        state.genreMapping
      ),
      needsUpdate: {
        ...state.needsUpdate,
        banner: false
      }
    }
  } else if (action.type === "REFRESH_PKGS_GRID") {
    if (!state.needsUpdate.banner) {
      return state;
    } // no-op
    console.info("[ChlupStore] refreshing pkgs grid...")
    return {
      ...state,
      cmprGridChannelsAZ: getCmprGridChannelsAZ(
        state?.channels,
        state?.cache?.gridCategoriesAZ,
        state?.cache?.gridPackages,
        state?.myFreeKey
      ),
      cmprGridChannelsNum: getCmprGridChannelsNum(
        state?.channels,
        state?.cache?.gridCategories,
        state?.cache?.gridPackages,
        state?.myFreeKey
      ),
      cmprGridChannelsCategorical: getCmprGridChannelsCategorical(
        state?.channels,
        state?.cache?.gridCategories,
        state?.cache?.gridPackages,
        state?.myFreeKey
      ),
      needsUpdate: {
        ...state.needsUpdate,
        grid: false
      }
    }
  }

  // default case
  return state;
}
